import { ComponentType } from 'react';
import { FixedSizeListProps, FixedSizeList as _FixedSizeList_ } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@artstorefronts/ui';
import {
  Copy,
  Loader2,
  TrendingUp,
  TrendingDown,
  Trash2,
  RotateCcw,
  XCircle,
  ArrowUp,
  ArrowDown,
  Megaphone,
} from 'lucide-react';
import { ApolloCache, useQuery, gql, useMutation } from '@apollo/client';
import { toast } from 'sonner';
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { postRowLabel } from '@arthelper/shared/plain-snippet';

import AdminLayout from '../../../components/AdminLayout';
import {
  ADMIN_SET_TRENDING_PRIORITY_MUTATION,
  ADMIN_SET_ANNOUNCEMENT_MUTATION,
  ADMIN_DELETE_COMMUNITY_POST_MUTATION,
} from '../../../graphql/admin-community-mutations';

const ADMIN_COMMUNITY_POSTS_QUERY = gql`
  query AdminCommunityPosts(
    $limit: Int
    $offset: Int
    $sortBy: String
    $sortDir: String
    $trendingPriority: String
    $communityId: ID
    $search: String
    $isAnnouncement: Boolean
  ) {
    adminCommunityPosts(
      limit: $limit
      offset: $offset
      sortBy: $sortBy
      sortDir: $sortDir
      trendingPriority: $trendingPriority
      communityId: $communityId
      search: $search
      isAnnouncement: $isAnnouncement
    ) {
      posts {
        id
        title
        content
        slug
        status
        voteScore
        commentCount
        trendingPriority
        isAnnouncement
        createdAt
        author {
          asfId
          firstName
          lastName
          email
        }
        community {
          id
          name
          slug
        }
      }
      total
    }
  }
`;

const ADMIN_COMMUNITY_LIST_QUERY = gql`
  query AdminCommunityList {
    adminCommunities(limit: 500, offset: 0, sortBy: "name", sortDir: "ASC") {
      communities {
        id
        name
      }
    }
  }
`;

const ADMIN_COMMUNITIES_QUERY = gql`
  query AdminCommunities($limit: Int, $offset: Int, $sortBy: String, $sortDir: String) {
    adminCommunities(limit: $limit, offset: $offset, sortBy: $sortBy, sortDir: $sortDir) {
      communities {
        id
        name
        slug
        description
        memberCount
        postCount
        createdAt
        members {
          user {
            asfId
            firstName
            lastName
            email
          }
        }
      }
      total
    }
  }
`;

const ADMIN_DELETE_COMMUNITY_MUTATION = gql`
  mutation AdminDeleteCommunity($communityId: ID!, $confirmName: String!) {
    adminDeleteCommunity(communityId: $communityId, confirmName: $confirmName)
  }
`;

const List = _FixedSizeList_ as unknown as ComponentType<FixedSizeListProps>;

// One template for the header and the data rows. Two copies of this string
// drifting apart is how the grid silently misaligns.
//
// The fixed tracks have a hard ceiling: AdminLayout's max-w-7xl (1280px) card is
// overflow-hidden, so there is no horizontal scroll and every pixel spent on a fixed
// track is taken from the post-title 1fr. Fixed total (936) + 9 gap-4 gaps (144) +
// px-4 (32) = 1112, which leaves the title ~168px at 1280px. A new column narrows
// the existing tracks to stay inside that budget rather than appending its own width.
const POSTS_GRID_COLS = 'grid-cols-[176px_1fr_96px_72px_64px_72px_72px_104px_104px_176px]';

// '_all' omits the argument, which leaves the grid unfiltered.
const ANNOUNCEMENT_FILTERS: Record<string, boolean | undefined> = {
  _all: undefined,
  announced: true,
  'not-announced': false,
};

type TabType = 'posts' | 'communities';

export default function AdminCommunityPage() {
  const { t } = useTranslation('admin');
  const [activeTab, setActiveTab] = useState<TabType>('posts');

  return (
    <AdminLayout title={t('community.page-title')} description={t('community.page-description')}>
      <>
        <div className="flex gap-4 border-b mb-4">
          <button
            onClick={() => setActiveTab('posts')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'posts'
                ? 'border-gray-900 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('community.tab-posts')}
          </button>
          <button
            onClick={() => setActiveTab('communities')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'communities'
                ? 'border-gray-900 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('community.tab-communities')}
          </button>
        </div>

        {activeTab === 'posts' ? <PostsTab /> : <CommunitiesTab />}
      </>
    </AdminLayout>
  );
}

function PostsTab() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [announcementFilter, setAnnouncementFilter] = useState<string>('_all');
  const [communityFilter, setCommunityFilter] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortDir, setSortDir] = useState<string>('DESC');
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 50;

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  const { data: communitiesData } = useQuery(ADMIN_COMMUNITY_LIST_QUERY);
  const communities: { id: string; name: string }[] = communitiesData?.adminCommunities?.communities ?? [];

  const queryVars = {
    limit: PAGE_SIZE,
    offset: 0,
    sortBy,
    sortDir,
    trendingPriority: priorityFilter || undefined,
    isAnnouncement: ANNOUNCEMENT_FILTERS[announcementFilter],
    communityId: communityFilter || undefined,
    search: debouncedSearch || undefined,
  };

  const { data, loading, fetchMore, refetch } = useQuery(ADMIN_COMMUNITY_POSTS_QUERY, {
    variables: queryVars,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const allPosts = data?.adminCommunityPosts?.posts || [];
  const totalPosts = data?.adminCommunityPosts?.total || 0;
  const hasMoreData = allPosts.length < totalPosts && totalPosts > 0;

  const loadMorePosts = useCallback(async () => {
    if (!hasMoreData || loading || loadingMore || allPosts.length >= totalPosts) return;

    setLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          limit: PAGE_SIZE,
          offset: allPosts.length,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.adminCommunityPosts?.posts?.length) {
            return prev;
          }

          const existingIds = new Set(prev.adminCommunityPosts.posts.map((post: any) => post.id));
          const newPosts = fetchMoreResult.adminCommunityPosts.posts.filter((post: any) => !existingIds.has(post.id));

          if (newPosts.length === 0) {
            return prev;
          }

          return {
            adminCommunityPosts: {
              ...prev.adminCommunityPosts,
              posts: [...prev.adminCommunityPosts.posts, ...newPosts],
              total: Math.max(prev.adminCommunityPosts.total, fetchMoreResult.adminCommunityPosts.total),
            },
          };
        },
      });
    } catch (error) {
      console.error('Error loading more posts:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchMore, allPosts.length, hasMoreData, loading, loadingMore, totalPosts]);

  const Row = ({ index, style }: any) => {
    const post = allPosts[index];
    // The title cell is this row's only link to the post, so a titleless post
    // labels it with its body and, failing that, its slug.
    const postLabel = postRowLabel(post.title, post.content) ?? post.slug;

    useEffect(() => {
      if (index >= allPosts.length - 5 && hasMoreData && !loadingMore) {
        loadMorePosts();
      }
    }, [index, allPosts.length, hasMoreData, loadMorePosts, loadingMore]);

    return (
      <div style={style} className="border-b">
        <div className={`grid ${POSTS_GRID_COLS} items-center px-4 py-3 text-xs gap-4`}>
          <div className="flex items-center gap-2">
            <div className="truncate" title={post.author?.email ?? t('community.deleted-user')}>
              <div className="font-medium">
                {post.author ? (
                  <>
                    {post.author.firstName} {post.author.lastName}
                  </>
                ) : (
                  <span className="italic text-gray-400">{t('community.deleted-user')}</span>
                )}
              </div>
              <div className="text-gray-500">{post.author?.email}</div>
            </div>
            {post.author?.email && (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(post.author.email);
                  toast.success(t('community.email-copied'));
                }}
                className="text-gray-500 hover:text-gray-700 flex-shrink-0"
              >
                <Copy className="h-3 w-3" />
              </button>
            )}
          </div>
          <div className="truncate" title={postLabel}>
            {post.community ? (
              <a
                href={`${__TOWN_SQUARE_URL__}/community/${post.community.slug}/post/${post.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:text-blue-700 font-medium"
              >
                {postLabel}
              </a>
            ) : (
              <span className="text-gray-400 font-medium">{postLabel}</span>
            )}
          </div>
          <div className="truncate text-gray-600" title={post.community?.name ?? t('community.deleted-community')}>
            {post.community?.name ?? <span className="italic text-gray-400">{t('community.deleted-community')}</span>}
          </div>
          <div className="text-right">{new Date(post.createdAt).toLocaleDateString()}</div>
          <div className="text-center">{post.voteScore}</div>
          <div className="text-center">{post.commentCount}</div>
          <StatusBadge status={post.status} />
          <TrendingPriorityBadge priority={post.trendingPriority} status={post.status} />
          <AnnouncementBadge isAnnouncement={post.isAnnouncement} status={post.status} />
          <PostActions post={post} queryVariables={queryVars} />
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4 flex-wrap">
          <Input
            placeholder={t('community.posts.search-placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-48 text-sm h-9"
          />
          <Select value={communityFilter || '_all'} onValueChange={(v) => setCommunityFilter(v === '_all' ? '' : v)}>
            <SelectTrigger className="w-52 text-sm overflow-hidden">
              <SelectValue placeholder={t('community.posts.filter-all-communities')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">{t('community.posts.filter-all-communities')}</SelectItem>
              {communities.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={priorityFilter || '_all'} onValueChange={(v) => setPriorityFilter(v === '_all' ? '' : v)}>
            <SelectTrigger className="w-36 text-sm">
              <SelectValue placeholder={t('community.posts.filter-all-priorities')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">{t('community.posts.filter-all-priorities')}</SelectItem>
              <SelectItem value="high">{t('community.posts.priority-high')}</SelectItem>
              <SelectItem value="normal">{t('community.posts.priority-normal')}</SelectItem>
              <SelectItem value="suppress">{t('community.posts.priority-suppress')}</SelectItem>
              <SelectItem value="remove">{t('community.posts.priority-remove')}</SelectItem>
              <SelectItem value="null">{t('community.posts.priority-none')}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={announcementFilter} onValueChange={setAnnouncementFilter}>
            <SelectTrigger className="w-44 text-sm">
              <SelectValue placeholder={t('community.posts.filter-all-announcements')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">{t('community.posts.filter-all-announcements')}</SelectItem>
              <SelectItem value="announced">{t('community.posts.filter-announced')}</SelectItem>
              <SelectItem value="not-announced">{t('community.posts.filter-not-announced')}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-36 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="createdAt">{t('community.posts.sort-created-at')}</SelectItem>
              <SelectItem value="voteScore">{t('community.posts.sort-score')}</SelectItem>
              <SelectItem value="commentCount">{t('community.posts.sort-comments')}</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={() => setSortDir((d) => (d === 'DESC' ? 'ASC' : 'DESC'))}
            title={sortDir === 'DESC' ? t('community.posts.sort-descending') : t('community.posts.sort-ascending')}
          >
            {sortDir === 'DESC' ? <ArrowDown className="h-4 w-4" /> : <ArrowUp className="h-4 w-4" />}
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-600">{t('community.posts.count', { count: totalPosts })}</div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={loading} className="h-9 gap-1.5">
            <RotateCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            {tc('refresh')}
          </Button>
        </div>
      </div>

      <div className="rounded-md border bg-white overflow-hidden">
        <div className="bg-gray-50 border-b">
          <div className={`grid ${POSTS_GRID_COLS} py-3 px-4 text-xs font-medium gap-4`}>
            <div>{t('community.posts.table.author')}</div>
            <div>{t('community.posts.table.post-title')}</div>
            <div>{t('community.posts.table.community')}</div>
            <div className="text-right">{t('community.posts.table.created-at')}</div>
            <div className="text-center">{t('community.posts.table.score')}</div>
            <div className="text-center">{t('community.posts.table.comments')}</div>
            <div className="text-center">{t('community.posts.table.status')}</div>
            <div className="text-center">{t('community.posts.table.priority')}</div>
            <div className="text-center">{t('community.posts.table.announcement')}</div>
            <div className="text-center">{t('community.posts.table.actions')}</div>
          </div>
        </div>
        <div style={{ height: 'calc(100dvh - 550px)' }}>
          <AutoSizer>
            {({ height, width }) => (
              <List height={height} itemCount={allPosts.length} itemSize={48} width={width}>
                {Row}
              </List>
            )}
          </AutoSizer>
        </div>
        {loadingMore && (
          <div className="flex items-center justify-center py-1">
            <span className="text-xs text-gray-600">{tc('loading')}</span>
          </div>
        )}
      </div>
    </>
  );
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation('admin');
  const normalized = status?.toLowerCase() ?? '';
  const colorMap: Record<string, string> = {
    approved: 'text-green-700',
    pending: 'text-yellow-700',
    hidden: 'text-red-700',
  };
  const color = colorMap[normalized] ?? 'text-gray-500';
  const label =
    normalized === 'approved' || normalized === 'pending' || normalized === 'hidden'
      ? t(`community.posts.post-status.${normalized}`)
      : normalized.charAt(0).toUpperCase() + normalized.slice(1);
  return (
    <div className="text-center">
      <span className={`text-xs font-medium ${color}`}>{label}</span>
    </div>
  );
}

function TrendingPriorityBadge({ priority, status }: { priority: string | null; status: string }) {
  const { t } = useTranslation('admin');
  if (!priority) {
    return <div className="text-center text-gray-400">{t('community.posts.priority-none')}</div>;
  }

  const colorMap = {
    high: 'bg-green-100 text-green-700',
    normal: 'bg-blue-100 text-blue-700',
    suppress: 'bg-amber-100 text-amber-700',
    remove: 'bg-red-100 text-red-700',
  };

  const color = colorMap[priority as keyof typeof colorMap] || 'bg-gray-100 text-gray-700';
  const isIneffective = priority !== 'remove' && status?.toLowerCase() !== 'approved';
  const knownPriorities = ['high', 'normal', 'suppress', 'remove'] as const;
  const priorityLabel = (knownPriorities as readonly string[]).includes(priority)
    ? t(`community.posts.priority-${priority}`)
    : priority.charAt(0).toUpperCase() + priority.slice(1);

  return (
    <div className="text-center flex items-center justify-center gap-1">
      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${color}`}>{priorityLabel}</span>
      {isIneffective && (
        <span title={t('community.posts.priority-ineffective')} className="text-amber-500 cursor-help">
          ⚠
        </span>
      )}
    </div>
  );
}

function AnnouncementBadge({ isAnnouncement, status }: { isAnnouncement: boolean; status: string }) {
  const { t } = useTranslation('admin');
  if (!isAnnouncement) {
    return <div className="text-center text-gray-400">{t('community.posts.announcement-none')}</div>;
  }

  // The flag survives moderation, but the public strip only reads approved
  // posts — so a flagged post that is not approved shows nowhere.
  const isIneffective = status?.toLowerCase() !== 'approved';

  return (
    <div className="text-center flex items-center justify-center gap-1">
      <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
        {t('community.posts.announcement-live')}
      </span>
      {isIneffective && (
        <span title={t('community.posts.announcement-ineffective')} className="text-amber-500 cursor-help">
          ⚠
        </span>
      )}
    </div>
  );
}

// Drops a row the active filter no longer matches. This page has no
// refetchQueries, so the cached page IS the grid.
function dropPostFromCache(cache: ApolloCache<any>, postId: string, queryVariables: any) {
  const cached = cache.readQuery<any>({
    query: ADMIN_COMMUNITY_POSTS_QUERY,
    variables: queryVariables,
  });
  if (!cached?.adminCommunityPosts) return;

  cache.writeQuery({
    query: ADMIN_COMMUNITY_POSTS_QUERY,
    variables: queryVariables,
    data: {
      adminCommunityPosts: {
        ...cached.adminCommunityPosts,
        posts: cached.adminCommunityPosts.posts.filter((p: any) => p.id !== postId),
        total: Math.max(0, cached.adminCommunityPosts.total - 1),
      },
    },
  });
}

function PostActions({ post, queryVariables }: { post: any; queryVariables: any }) {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [setPriority, { loading: settingPriority }] = useMutation(ADMIN_SET_TRENDING_PRIORITY_MUTATION);
  const [setAnnouncement, { loading: settingAnnouncement }] = useMutation(ADMIN_SET_ANNOUNCEMENT_MUTATION);
  const [deletePost, { loading: removing }] = useMutation(ADMIN_DELETE_COMMUNITY_POST_MUTATION);

  const handleSetPriority = async (priority: string | null) => {
    try {
      await setPriority({
        variables: {
          postId: post.id,
          priority,
          reason: priority
            ? t('community.posts.priority-set-reason', { priority })
            : t('community.posts.priority-reset-reason'),
        },
        optimisticResponse: {
          adminSetCommunityPostTrendingPriority: {
            __typename: 'CommunityPost',
            id: post.id,
            trendingPriority: priority ? priority.toLowerCase() : null,
          },
        },
        update(cache) {
          const activeFilter = queryVariables.trendingPriority;
          if (!activeFilter) return;

          const newPriority = priority ? priority.toLowerCase() : null;
          const filterMatchesNew = activeFilter === 'null' ? newPriority === null : newPriority === activeFilter;
          if (filterMatchesNew) return;

          dropPostFromCache(cache, post.id, queryVariables);
        },
      });
      toast.success(
        priority ? t('community.posts.toast-priority-set', { priority }) : t('community.posts.toast-priority-reset')
      );
    } catch (error) {
      console.error(error);
      toast.error(t('community.posts.toast-priority-failed'));
    }
  };

  const handleToggleAnnouncement = async () => {
    const isAnnouncement = !post.isAnnouncement;
    try {
      await setAnnouncement({
        variables: { postId: post.id, isAnnouncement },
        optimisticResponse: {
          adminSetCommunityPostAnnouncement: {
            __typename: 'CommunityPost',
            id: post.id,
            isAnnouncement,
          },
        },
        update(cache) {
          const activeFilter = queryVariables.isAnnouncement;
          if (activeFilter === undefined) return;
          if (activeFilter === isAnnouncement) return;

          dropPostFromCache(cache, post.id, queryVariables);
        },
      });
      toast.success(
        isAnnouncement ? t('community.posts.toast-announced') : t('community.posts.toast-announcement-cleared')
      );
    } catch (error) {
      console.error(error);
      // The server owns the cap and the private-community refusal, so its
      // message is what the admin needs to read.
      const reason = error instanceof Error ? error.message : '';
      toast.error(reason || t('community.posts.toast-announcement-failed'));
    }
  };

  const handleRemovePost = async () => {
    try {
      await deletePost({
        variables: { postId: post.id },
        update(cache) {
          dropPostFromCache(cache, post.id, queryVariables);
        },
      });
      toast.success(t('community.posts.toast-deleted'));
      setConfirmRemove(false);
    } catch (error) {
      console.error(error);
      toast.error(t('community.posts.toast-delete-failed'));
    }
  };

  return (
    <>
      <div className="flex items-center justify-center gap-2">
        <button
          onClick={() => handleSetPriority('HIGH')}
          className="text-green-600 hover:text-green-700 disabled:opacity-40"
          title={t('community.posts.action-high-priority')}
          disabled={settingPriority}
        >
          <TrendingUp className="h-4 w-4" />
        </button>
        <button
          onClick={() => handleSetPriority('NORMAL')}
          className="text-blue-600 hover:text-blue-700 disabled:opacity-40"
          title={t('community.posts.action-normal-priority')}
          disabled={settingPriority}
        >
          <TrendingUp className="h-4 w-4" />
        </button>
        <button
          onClick={() => handleSetPriority('SUPPRESS')}
          className="text-amber-600 hover:text-amber-700 disabled:opacity-40"
          title={t('community.posts.action-suppress')}
          disabled={settingPriority}
        >
          <TrendingDown className="h-4 w-4" />
        </button>
        <button
          onClick={() => handleSetPriority('REMOVE')}
          className="text-red-600 hover:text-red-700 disabled:opacity-40"
          title={t('community.posts.action-remove-trending')}
          disabled={settingPriority}
        >
          <XCircle className="h-4 w-4" />
        </button>
        <button
          onClick={() => handleSetPriority(null)}
          className={`text-gray-600 hover:text-gray-700 disabled:opacity-40 ${!post.trendingPriority ? 'invisible' : ''}`}
          title={t('community.posts.action-reset-priority')}
          disabled={settingPriority || !post.trendingPriority}
        >
          <RotateCcw className="h-4 w-4" />
        </button>
        <button
          onClick={handleToggleAnnouncement}
          className={`disabled:opacity-40 ${
            post.isAnnouncement ? 'text-amber-600 hover:text-amber-700' : 'text-gray-600 hover:text-gray-700'
          }`}
          title={
            post.isAnnouncement ? t('community.posts.action-clear-announcement') : t('community.posts.action-announce')
          }
          disabled={settingAnnouncement}
        >
          <Megaphone className="h-4 w-4" />
        </button>
        <button
          onClick={() => setConfirmRemove(true)}
          className="text-red-600 hover:text-red-700"
          title={t('community.posts.action-remove-post')}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      <AlertDialog open={confirmRemove} onOpenChange={setConfirmRemove}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('community.posts.delete-dialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('community.posts.delete-dialog.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tc('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemovePost} disabled={removing} className="bg-red-600 hover:bg-red-700">
              {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : t('community.posts.delete-dialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function CommunitiesTab() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortDir, setSortDir] = useState<string>('DESC');
  const [loadingMore, setLoadingMore] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [confirmText, setConfirmText] = useState('');
  const PAGE_SIZE = 50;

  const { data, loading, fetchMore } = useQuery(ADMIN_COMMUNITIES_QUERY, {
    variables: {
      limit: PAGE_SIZE,
      offset: 0,
      sortBy,
      sortDir,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const [deleteCommunity, { loading: deleting }] = useMutation(ADMIN_DELETE_COMMUNITY_MUTATION);

  const allCommunities = data?.adminCommunities?.communities || [];
  const totalCommunities = data?.adminCommunities?.total || 0;
  const hasMoreData = allCommunities.length < totalCommunities && totalCommunities > 0;

  const loadMoreCommunities = useCallback(async () => {
    if (!hasMoreData || loading || loadingMore || allCommunities.length >= totalCommunities) return;

    setLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          limit: PAGE_SIZE,
          offset: allCommunities.length,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.adminCommunities?.communities?.length) {
            return prev;
          }

          const existingIds = new Set(prev.adminCommunities.communities.map((c: any) => c.id));
          const newCommunities = fetchMoreResult.adminCommunities.communities.filter(
            (c: any) => !existingIds.has(c.id)
          );

          if (newCommunities.length === 0) {
            return prev;
          }

          return {
            adminCommunities: {
              ...prev.adminCommunities,
              communities: [...prev.adminCommunities.communities, ...newCommunities],
              total: Math.max(prev.adminCommunities.total, fetchMoreResult.adminCommunities.total),
            },
          };
        },
      });
    } catch (error) {
      console.error('Error loading more communities:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchMore, allCommunities.length, hasMoreData, loading, loadingMore, totalCommunities]);

  const handleDelete = async () => {
    if (!deleteTarget || confirmText !== deleteTarget.name) return;

    try {
      await deleteCommunity({
        variables: {
          communityId: deleteTarget.id,
          confirmName: confirmText,
        },
        update(cache, { data }) {
          if (!data?.adminDeleteCommunity) return;

          const queryVars = { limit: PAGE_SIZE, offset: 0, sortBy, sortDir };
          const cached = cache.readQuery<any>({
            query: ADMIN_COMMUNITIES_QUERY,
            variables: queryVars,
          });
          if (!cached?.adminCommunities) return;

          cache.writeQuery({
            query: ADMIN_COMMUNITIES_QUERY,
            variables: queryVars,
            data: {
              adminCommunities: {
                ...cached.adminCommunities,
                communities: cached.adminCommunities.communities.filter((c: any) => c.id !== deleteTarget.id),
                total: Math.max(0, cached.adminCommunities.total - 1),
              },
            },
          });
        },
      });
      toast.success(t('community.communities.toast-deleted'));
      setDeleteTarget(null);
      setConfirmText('');
    } catch (error) {
      console.error(error);
      toast.error(t('community.communities.toast-delete-failed'));
    }
  };

  const Row = ({ index, style }: any) => {
    const community = allCommunities[index];
    const owner = community.members?.[0]?.user;

    useEffect(() => {
      if (index >= allCommunities.length - 5 && hasMoreData && !loadingMore) {
        loadMoreCommunities();
      }
    }, [index, allCommunities.length, hasMoreData, loadMoreCommunities, loadingMore]);

    return (
      <div style={style} className="border-b">
        <div className="grid grid-cols-[200px_150px_1fr_80px_80px_80px_80px] items-center px-4 py-3 text-xs gap-4">
          <div className="flex items-center gap-2">
            {owner ? (
              <>
                <div className="truncate" title={owner.email}>
                  <div className="font-medium">
                    {owner.firstName} {owner.lastName}
                  </div>
                  <div className="text-gray-500">{owner.email}</div>
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(owner.email);
                    toast.success(t('community.email-copied'));
                  }}
                  className="text-gray-500 hover:text-gray-700 flex-shrink-0"
                >
                  <Copy className="h-3 w-3" />
                </button>
              </>
            ) : (
              <div className="text-gray-400">{t('community.communities.no-owner')}</div>
            )}
          </div>
          <div className="font-medium truncate" title={community.name}>
            {community.name}
          </div>
          <div className="truncate text-gray-600" title={community.description || ''}>
            {community.description || t('community.communities.no-description')}
          </div>
          <div className="text-center">{community.memberCount}</div>
          <div className="text-center">{community.postCount}</div>
          <div className="text-right">{new Date(community.createdAt).toLocaleDateString()}</div>
          <div className="flex items-center justify-center">
            <button
              onClick={() => setDeleteTarget(community)}
              className="text-red-600 hover:text-red-700"
              title={t('community.communities.delete-community')}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-36 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="createdAt">{t('community.posts.sort-created-at')}</SelectItem>
              <SelectItem value="memberCount">{t('community.communities.sort-members')}</SelectItem>
              <SelectItem value="postCount">{t('community.communities.sort-posts')}</SelectItem>
              <SelectItem value="name">{t('community.communities.sort-name')}</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={() => setSortDir((d) => (d === 'DESC' ? 'ASC' : 'DESC'))}
            title={sortDir === 'DESC' ? t('community.posts.sort-descending') : t('community.posts.sort-ascending')}
          >
            {sortDir === 'DESC' ? <ArrowDown className="h-4 w-4" /> : <ArrowUp className="h-4 w-4" />}
          </Button>
        </div>
        <div className="text-sm text-gray-600">{t('community.communities.count', { count: totalCommunities })}</div>
      </div>

      <div className="rounded-md border bg-white overflow-hidden">
        <div className="bg-gray-50 border-b">
          <div className="grid grid-cols-[200px_150px_1fr_80px_80px_80px_80px] py-3 px-4 text-xs font-medium gap-4">
            <div>{t('community.communities.table.owner')}</div>
            <div>{t('community.communities.table.name')}</div>
            <div>{t('community.communities.table.description')}</div>
            <div className="text-center">{t('community.communities.table.members')}</div>
            <div className="text-center">{t('community.communities.table.posts')}</div>
            <div className="text-right">{t('community.communities.table.created-at')}</div>
            <div className="text-center">{t('community.communities.table.actions')}</div>
          </div>
        </div>
        <div style={{ height: 'calc(100dvh - 550px)' }}>
          <AutoSizer>
            {({ height, width }) => (
              <List height={height} itemCount={allCommunities.length} itemSize={48} width={width}>
                {Row}
              </List>
            )}
          </AutoSizer>
        </div>
        {loadingMore && (
          <div className="flex items-center justify-center py-1">
            <span className="text-xs text-gray-600">{tc('loading')}</span>
          </div>
        )}
      </div>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setDeleteTarget(null);
            setConfirmText('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('community.communities.delete-dialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('community.communities.delete-dialog.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium mb-2 block">
              {t('community.communities.delete-dialog.confirm-label-before')}
              <span className="font-bold">{deleteTarget?.name}</span>
              {t('community.communities.delete-dialog.confirm-label-after')}
            </label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={t('community.communities.delete-dialog.confirm-placeholder')}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmText('')}>{tc('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={confirmText !== deleteTarget?.name || deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {tc('deleting')}
                </>
              ) : (
                tc('delete')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
